import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './styles/completed.module.css';
import sharedStyles from './styles/gameCard.module.css';
import { formatLine, calculateMargin, calculateOverUnderDiff, getScoreColor } from '../../utils/lineDataUtils';
import { useGameContext } from '../../context/GameContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import HiddenText from '../HiddenText';

const CompletedGameCard = ({ game, displayedWeek }) => {

  const { locksCache, user, getLocksForWeek, getMaxPicksForWeek } = useGameContext();
  const { canSeeLocks, canSeeLockers } = useLeagueSettings();

  // Check if current user has reached max picks for this week
  const hasReachedMaxPicks = useCallback(() => {
    const locksForWeek = getLocksForWeek(displayedWeek);
    const maxPicksForWeek = getMaxPicksForWeek(displayedWeek);

    if (!locksForWeek || !Array.isArray(locksForWeek)) return false;

    const userLocksCount = locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    return userLocksCount >= maxPicksForWeek;
  }, [getLocksForWeek, displayedWeek, user, getMaxPicksForWeek]);

  const homeScore = game.home_score ?? 0;
  const awayScore = game.away_score ?? 0;

  const spread = game.betting.spread;
  const homeMargin = calculateMargin(homeScore, awayScore, spread);
  const awayMargin = -homeMargin;
  const totalScore = homeScore + awayScore;
  const overUnderDiff = calculateOverUnderDiff(homeScore, awayScore, game.betting.over_under);

  const getMarginColor = (margin) => {
    if (margin >= 0) return styles.positiveMargin;
    if (margin < 0) return styles.negativeMargin;
    return '';
  };

  // Get locks for this specific game
  const getGameLocks = useCallback(() => {
    const locksForWeek = locksCache[displayedWeek] || [];
    return locksForWeek.filter(lock => lock.gameId === game.id)
      .map(lock => ({
        ...lock,
        username: lock.username || 'Unknown User',
        lockLine: lock.lockLine,
      }))
      .sort((a, b) => {
        // Sort by pick type: away, home, over, under
        const pickOrder = { away: 0, home: 1, over: 2, under: 3 };
        return pickOrder[a.pickType] - pickOrder[b.pickType];
      });
  }, [displayedWeek, locksCache, game]);

  const gameLocks = getGameLocks();

  const getPickLabel = (pickType, game) => {
    switch (pickType) {
      case 'home': return game.home_team.abbreviation || game.home_team.name;
      case 'away': return game.away_team.abbreviation || game.away_team.name;
      case 'over': return 'Over';
      case 'under': return 'Under';
      default: return '';
    }
  };

  return (
    <div className={`${sharedStyles.gameCard} ${styles.completedGameCard}`}>
      <div className={styles.gridContainer}>
        {/* Header */}
        <div className={styles.headerRow}>
          <div className={styles.teamCol}></div>
          <div className={styles.dataCol}>SCORE</div>
          <div className={styles.dataCol}>CLOSING<br/>LINE</div>
          <div className={styles.dataCol}>BETTING<br/>MARGIN</div>
        </div>

        {/* Away Team */}
        <div className={styles.gameRow}>
          <div className={styles.teamCol}>
            <div className={styles.teamName}>
              {game.away_team.rank &&
                <span className={`${sharedStyles.rank} ${styles.rank}`}>
                  ({game.away_team.rank})
                </span>
              }
              <span className={styles.schoolName}>{game.away_team.name}</span>
            </div>
            <span className={styles.record}>{game.away_team.record}</span>
          </div>
          <div className={`${styles.dataCol} ${styles[getScoreColor(awayScore, homeScore)]}`}>
            {awayScore}
          </div>
          <div className={styles.dataCol}>{formatLine(-spread)}</div>
          <div className={`${styles.dataCol} ${getMarginColor(awayMargin)}`}>
            {formatLine(awayMargin)}
          </div>
        </div>

        {/* Home Team */}
        <div className={styles.gameRow}>
          <div className={styles.teamCol}>
            <div className={styles.teamName}>
              {game.home_team.rank &&
                <span className={`${sharedStyles.rank} ${styles.rank}`}>
                  ({game.home_team.rank})
                </span>
              }
              <span className={styles.schoolName}>{game.home_team.name}</span>
            </div>
            <span className={styles.record}>{game.home_team.record}</span>
          </div>
          <div className={`${styles.dataCol} ${styles[getScoreColor(homeScore, awayScore)]}`}>
            {homeScore}
          </div>
          <div className={styles.dataCol}>{formatLine(spread)}</div>
          <div className={`${styles.dataCol} ${getMarginColor(homeMargin)}`}>
            {formatLine(homeMargin)}
          </div>
        </div>

        {/* O/U Row */}
        {game.betting.over_under && (
          <div className={`${styles.gameRow} ${styles.totalRow}`}>
            <div className={styles.teamCol}>O/U</div>
            <div className={styles.dataCol}>{totalScore}</div>
            <div className={styles.dataCol}>{game.betting.over_under}</div>
            <div className={styles.dataCol}>
              {`${overUnderDiff > 0 ? 'O' : 'U'} ${Math.abs(overUnderDiff).toFixed(1).replace('.0', '')}`}
            </div>
          </div>
        )}
      </div>

      {/* Lock Status Bars */}
      {gameLocks.length > 0 && (canSeeLocks() || hasReachedMaxPicks()) && (
        <div className={styles.lockStatusBars}>
          {gameLocks.map((lock, index) => {
            const pickLabel = getPickLabel(lock.pickType, game);
            const line = lock.pickType === 'over' || lock.pickType === 'under' ?
                        lock.lockLine :
                        formatLine(lock.lockLine);

            // Determine what to show based on league settings and user state
            const isUserLock = lock.userId === user?.id;
            const showUsername = isUserLock || canSeeLockers(displayedWeek, isUserLock, 0, 1) || hasReachedMaxPicks();
            const showPickInfo = canSeeLocks() || hasReachedMaxPicks();

            return (
              <div
                key={`${lock.userId}-${lock.pickType}`}
                className={`${styles.lockStatusBar} ${lock.won_lost?.toLowerCase() === 'won' ? styles.win : styles.loss}`}
              >
                <span className={styles.userSection}>
                  {showUsername ? (
                    lock.username
                  ) : (
                    <HiddenText
                      type="locker"
                      weekNumber={displayedWeek}
                      showTooltip={true}
                    />
                  )}
                </span>
                <div className={styles.pickSection}>
                  {showPickInfo ? (
                    <span>{pickLabel} {line}</span>
                  ) : (
                    <HiddenText
                      type="lock"
                      weekNumber={displayedWeek}
                      showTooltip={true}
                    />
                  )}
                  <span className={styles.resultBadge}>
                    {lock.won_lost ? lock.won_lost.charAt(0).toUpperCase() + lock.won_lost.slice(1) : 'Pending'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

CompletedGameCard.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.number.isRequired,
    betting: PropTypes.shape({
      spread: PropTypes.number.isRequired,
      over_under: PropTypes.number
    }).isRequired,
    home_score: PropTypes.number,
    away_score: PropTypes.number,
    home_team: PropTypes.shape({
      name: PropTypes.string.isRequired,
      rank: PropTypes.number,
      record: PropTypes.string
    }).isRequired,
    away_team: PropTypes.shape({
      name: PropTypes.string.isRequired,
      rank: PropTypes.number,
      record: PropTypes.string
    }).isRequired
  }).isRequired,
  displayedWeek: PropTypes.number.isRequired
};

export default CompletedGameCard;
